import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { tap, map } from 'rxjs/operators';

import { ApiService, HttpMethod } from '../common/api.service';
import { AuthenticatedUser } from './authenticated-user.model';
import { UserLoginResult } from './user-login-result.model';
import { UserRegistrationInfo } from './user-registration-info.model';
import { UserLoginInfo } from './user-login-info.model';
import { LocalStorageManagerService } from '../../core/services/local-storage-manager.service';

@Injectable({
    providedIn: 'root'
})
export class AuthApiService extends ApiService
{
    constructor(protected override http: HttpClient,
                private localCacheManager: LocalStorageManagerService)
    {
        super(http);
    }

    signIn(email: string, password: string): Observable<AuthenticatedUser>
    {
        const userLoginInfo = new UserLoginInfo(email, password);
        return this.apiRequest<AuthenticatedUser>(this.loginUrl, HttpMethod.Post, JSON.stringify(userLoginInfo))
            .pipe(
                tap(response => this.localCacheManager.userLoginResult = new UserLoginResult(response)),
                map(response => new AuthenticatedUser(response))
            );
    }

    signInWithRefreshToken(refreshToken: string, organizationId?: number)
    {
        let httpBody = `{ "grantType": "refresh_token", "refreshToken": "${refreshToken}", "LocationId": 1 }`;
        if (organizationId)
        {
            httpBody = `{ "grantType": "refresh_token", "RefreshToken": "${refreshToken}", "organizationId": ${organizationId} }`;
        }

        return this.apiRequest<AuthenticatedUser>(
            this.loginUrl, HttpMethod.Post, httpBody)
            .pipe(
                tap(response => this.localCacheManager.userLoginResult = new UserLoginResult(response)),
                map(response => new AuthenticatedUser(response)));
    }

    changePassword(oldPassword: string, newPassword: string)
    {
        return this.apiRequest(
            this.changePasswordUrl, HttpMethod.Post, `{ "oldPassword": "${oldPassword}", "newPassword": "${newPassword}" }`);
    }

    forgotPassword(userName: string)
    {
        return this.apiRequest(this.forgotPasswordUrl, HttpMethod.Post, `{ "username": "${userName}" }`);
    }

    resetPassword(userId: string, token: string, password: string)
    {
        const httpBody = `{ "userId": "${userId}", "token": "${token}", "password": "${password}" }`;
        return this.apiRequest(this.resetPasswordUrl, HttpMethod.Post, httpBody);
    }

    registerNewUser(userRegistrationInfo: UserRegistrationInfo)
    {
        return this.apiRequest(this.registerUserUrl, HttpMethod.Post, JSON.stringify(userRegistrationInfo));
    }

    confirmEmail(confirmInfo: { userId: string, token: string })
    {
        return this.apiRequest(this.confirmEmailUrl, HttpMethod.Post, JSON.stringify(confirmInfo));
    }

    confirmUserEmail(email: string): Observable<any>
    {
        return this.apiRequest(this.confirmUserEmailUrl, HttpMethod.Post, JSON.stringify({ email }));
    }

    setUserPassword(email: string, password: string): Observable<any>
    {
        return this.apiRequest(this.setUserPasswordUrl, HttpMethod.Post, JSON.stringify({ email, password }));
    }

    generateSupportCode(email: string): Observable<any>
    {
        return this.apiRequest(this.generateSupportCodeUrl, HttpMethod.Post, JSON.stringify({ email }));
    }

    // =========================================================================================================================================================
    // URLs
    // =========================================================================================================================================================
    /* tslint:disable:member-ordering */

    get loginUrl(): string { return `${this.baseAuthApiUrl}/token`; }
    get changePasswordUrl(): string { return `${this.baseAuthApiUrl}/changepassword`; }
    get confirmEmailUrl(): string { return `${this.baseAuthApiUrl}/confirm`; }
    get confirmUserEmailUrl(): string { return `${this.baseAuthApiUrl}/confirmUserEmail`; }
    get generateSupportCodeUrl(): string { return `${this.baseAuthApiUrl}/generateSupportLoginCode`; }
    get forgotPasswordUrl(): string { return `${this.baseAuthApiUrl}/forgotpassword`; }
    get registerUserUrl(): string { return `${this.baseAuthApiUrl}/register`; }
    get resetPasswordUrl(): string { return `${this.baseAuthApiUrl}/resetpassword`; }
    get setUserPasswordUrl(): string { return `${this.baseAuthApiUrl}/setUserPassword`; }
}
